import { useCallback, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { LicenseDto, TenantDetail } from '@order-pilot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Badge, LicenseStatusBadge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { SelectField, TextField } from '@/components/Form';
import { IconDevice, IconPlus } from '@/components/Icons';
import { Modal } from '@/components/Modal';
import { SectionCard } from '@/components/SectionCard';
import { EmptyState } from '@/components/States';
import { createDevice, tenantKeys } from '@/features/tenants/api';
import { fetchLicenses, licenseKeys } from '@/features/licenses/api';
import { getErrorMessage } from '@/lib/api';
import { formatDateTime, formatRelative } from '@/lib/format';

export interface DevicesSectionProps {
  tenant: TenantDetail;
}

export function DevicesSection({ tenant }: DevicesSectionProps) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [branchId, setBranchId] = useState('');
  const [labelError, setLabelError] = useState<string | undefined>(undefined);

  const navigate = useNavigate();

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: tenantKeys.all });
    void queryClient.invalidateQueries({ queryKey: licenseKeys.all });
  }, [queryClient]);

  // Licences are fetched here and joined by deviceId rather than added to the
  // devices endpoint: the same query already backs the "Awaiting activation"
  // card, so this costs nothing extra and the two views cannot disagree.
  const licensesQuery = useQuery({
    queryKey: licenseKeys.list(tenant.id),
    queryFn: ({ signal }) => fetchLicenses(tenant.id, signal),
  });

  const licensesByDevice = useMemo(() => {
    const map = new Map<string, LicenseDto>();
    for (const license of licensesQuery.data ?? []) {
      if (license.deviceId) map.set(license.deviceId, license);
    }
    return map;
  }, [licensesQuery.data]);

  const licenseFor = useCallback(
    (deviceId: string) => licensesByDevice.get(deviceId) ?? null,
    [licensesByDevice],
  );

  const deviceHref = useCallback(
    (deviceId: string) => `/cp/tenants/${tenant.id}/devices/${deviceId}`,
    [tenant.id],
  );

  const addMutation = useMutation({
    mutationFn: () => createDevice(tenant.id, { branchId, label: label.trim() }),
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
    },
  });


  const { reset: resetAdd } = addMutation;

  const branchName = useCallback(
    (id: string): string => tenant.branches.find((branch) => branch.id === id)?.name ?? '—',
    [tenant.branches],
  );

  const openAdd = useCallback(() => {
    setLabel('');
    setBranchId(tenant.branches[0]?.id ?? '');
    setLabelError(undefined);
    resetAdd();
    setAddOpen(true);
  }, [resetAdd, tenant.branches]);

  const handleAdd = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!label.trim()) {
        setLabelError('Device label is required.');
        return;
      }
      setLabelError(undefined);
      addMutation.mutate();
    },
    [addMutation, label],
  );

  const hasBranches = tenant.branches.length > 0;

  return (
    <>
      <SectionCard
        headingId="section-devices"
        title="Devices"
        count={tenant.devices.length}
        description="One per POS install. Open a till to see its licence, sync token and reporting switch, and to act on any of them."
        action={
          <Button
            size="sm"
            icon={<IconPlus className="h-3.5 w-3.5" />}
            onClick={openAdd}
            disabled={!hasBranches}
            title={hasBranches ? undefined : 'Add a branch first'}
          >
            Add device
          </Button>
        }
      >
        {tenant.devices.length === 0 ? (
          <EmptyState
            compact
            icon={<IconDevice className="h-5 w-5" />}
            title="No devices registered"
            description={
              hasBranches
                ? 'Register the till to issue its sync token — the token is shown once, at creation.'
                : 'Add a branch first, then register the till under it.'
            }
            action={
              hasBranches ? (
                <Button icon={<IconPlus className="h-4 w-4" />} onClick={openAdd}>
                  Add device
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-surface">
                <tr>
                  <th scope="col" className="th">
                    Device
                  </th>
                  <th scope="col" className="th">
                    Branch
                  </th>
                  <th scope="col" className="th">
                    Licence
                  </th>
                  <th scope="col" className="th">
                    Valid to
                  </th>
                  <th scope="col" className="th">
                    Reporting
                  </th>
                  <th scope="col" className="th">
                    Last sync
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tenant.devices.map((device) => {
                  const license = licenseFor(device.id);
                  return (
                    <tr
                      key={device.id}
                      onClick={() => navigate(deviceHref(device.id))}
                      className="cursor-pointer transition-colors hover:bg-slate-50"
                    >
                      <td className="td font-medium text-slate-900">
                        {/* A real link, not just a row handler: middle-click,
                            ⌘-click and keyboard navigation all have to work, and
                            an onClick alone gives none of them. */}
                        <Link
                          to={deviceHref(device.id)}
                          onClick={(event) => event.stopPropagation()}
                          className="rounded hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
                        >
                          {device.label}
                        </Link>
                      </td>
                      <td className="td text-slate-500">{branchName(device.branchId)}</td>
                      <td className="td">
                        {license ? (
                          <LicenseStatusBadge license={license} />
                        ) : (
                          <span
                            className="text-xs text-slate-400"
                            title="This till was registered by hand and no licence has activated onto it. It can sync, but has nothing to trade on."
                          >
                            None
                          </span>
                        )}
                      </td>
                      <td className="td whitespace-nowrap text-slate-700">
                        {license?.expiresAt ? formatDateTime(license.expiresAt) : '—'}
                      </td>
                      <td className="td">
                        <ReportingCell
                          on={device.reportingEnabled}
                          disconnected={device.revokedAt !== null}
                        />
                      </td>
                      <td
                        className="td whitespace-nowrap text-slate-500"
                        title={formatDateTime(device.lastSeenAt)}
                      >
                        {formatRelative(device.lastSeenAt, 'Never synced')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <Modal
        open={addOpen}
        title="Add device"
        description="Registers a POS install and issues its sync token."
        size="sm"
        onClose={addMutation.isPending ? () => undefined : () => setAddOpen(false)}
      >
        <form onSubmit={handleAdd} noValidate className="space-y-5">
          {addMutation.isError ? <Alert tone="error">{getErrorMessage(addMutation.error)}</Alert> : null}

          <TextField
            id="device-label"
            label="Label"
            required
            data-autofocus
            placeholder="Counter till"
            hint="How you will recognise this till in the console."
            value={label}
            onChange={(event) => {
              setLabel(event.target.value);
              setLabelError(undefined);
            }}
            error={labelError}
            disabled={addMutation.isPending}
          />

          <SelectField
            id="device-branch"
            label="Branch"
            required
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            disabled={addMutation.isPending}
          >
            {tenant.branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </SelectField>

          <Alert tone="info">
            No sync token is issued here. The till receives one when its licence is activated, so registering it
            now is safe to do ahead of time.
          </Alert>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button onClick={() => setAddOpen(false)} disabled={addMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={addMutation.isPending}>
              Create device
            </Button>
          </div>
        </form>
      </Modal>

    </>
  );
}

/**
 * Whether this till is sending data — the one question the list needs to answer.
 *
 * Sync connection and the reporting flag were separate columns, but they read as
 * the same thing at a glance and could contradict each other: a disconnected
 * till showing "Reporting: On" is describing an intention, not what is
 * happening. Disconnected therefore wins.
 *
 * Read-only. The SWITCH is on the till's own page, because a control that
 * mutates inside a row you click through to puts "open this" and "change this" a
 * few pixels apart.
 */
function ReportingCell({
  on,
  disconnected,
}: {
  on: boolean;
  disconnected: boolean;
}) {
  if (disconnected) {
    return (
      <Badge tone="danger" dot>
        Disconnected
      </Badge>
    );
  }
  return on ? (
    <Badge tone="success" dot>
      On
    </Badge>
  ) : (
    <span className="text-xs text-slate-500" title="Reporting is switched off for this till. Open it to turn it on.">
      Off
    </span>
  );
}
